import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, downloadBlob, errorMessage } from "@/lib/api";
import { InvoiceBadge, btnGold, btnLine, inp } from "@/lib/crm";
import { money } from "@/lib/pricing";
import { DateField } from "@/components/ui/date-field";

const today = () => new Date().toISOString().slice(0, 10);
const METHODS = ["cash", "check", "transfer", "card", "other"];

function PaymentForm({ inv, onSaved }) {
  const [f, setF] = useState({ date: today(), amount: "", method: "check", reference: "", note: "" });
  const [over, setOver] = useState(false);
  const save = async (confirm = false) => {
    const amount = Number(f.amount);
    if (!(amount > 0)) return toast.error("Enter an amount greater than 0");
    try {
      const { data } = await api.post(`/invoices/${inv.id}/payments`, { ...f, amount, confirm_over: confirm });
      toast.success("Payment recorded");
      setOver(false);
      setF({ ...f, amount: "", reference: "", note: "" });
      onSaved(data);
    } catch (e) {
      if (e.response?.status === 409) setOver(true);
      else toast.error(errorMessage(e));
    }
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-800 p-4" data-testid="payment-form">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Record payment</p>
      <div className="grid gap-2 sm:grid-cols-3">
        <DateField testId="payment-date-input" value={f.date} onChange={(v) => setF({ ...f, date: v })} className={inp} />
        <input data-testid="payment-amount-input" inputMode="decimal" placeholder="Amount" value={f.amount} onChange={(e) => { setF({ ...f, amount: e.target.value }); setOver(false); }} className={`${inp} font-mono-num`} />
        <select data-testid="payment-method-select" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className={`${inp} capitalize`}>
          {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input data-testid="payment-reference-input" placeholder="Reference (check #…)" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={inp} />
        <input data-testid="payment-note-input" placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={`${inp} sm:col-span-2`} />
      </div>
      {over && (
        <div data-testid="payment-over-warning" className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-3 text-sm text-amber-700">
          Payment is more than the balance ({money(inv.balance)}). The extra is recorded as a credit.
          <button data-testid="payment-over-confirm" onClick={() => save(true)} className="ml-2 font-semibold underline">Record anyway</button>
        </div>
      )}
      <button data-testid="payment-save-button" onClick={() => save(false)} className={btnGold}>Record payment</button>
    </div>
  );
}

function Reason({ label, testId, onSubmit }) {
  const [r, setR] = useState("");
  return (
    <div className="flex gap-2">
      <input data-testid={`${testId}-reason-input`} placeholder="Reason" value={r} onChange={(e) => setR(e.target.value)} className={`${inp} h-9`} />
      <button data-testid={`${testId}-confirm`} disabled={!r.trim()} onClick={() => onSubmit(r)} className={btnLine}>{label}</button>
    </div>
  );
}

export function InvoiceDialog({ invoice, onClose, onChanged }) {
  const { user } = useAuth();
  const admin = user?.role === "owner";
  const [inv, setInv] = useState(invoice);
  const [del, setDel] = useState(null);
  const [voiding, setVoiding] = useState(false);
  const update = (d) => { setInv(d); onChanged?.(); };
  const act = async (fn, msg) => { try { update((await fn()).data); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); } };
  const pays = inv.payments || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invoice-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-display text-2xl"><span data-testid="invoice-number" className="font-mono-num">{inv.number}</span> <InvoiceBadge inv={inv} testId="invoice-status" /></DialogTitle>
          <DialogDescription>{inv.label} · {inv.customer_name} · {inv.project_name} · Estimate {inv.quote_number}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 text-sm">
          {[["Amount", inv.amount, "invoice-amount"], ["Paid", inv.paid, "invoice-paid"], ["Balance", inv.balance, "invoice-balance"]].map(([l, v, t]) => (
            <div key={l} className="rounded-xl border border-slate-800 p-3"><p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p><p data-testid={t} className="font-mono-num text-lg font-semibold text-slate-100">{money(v)}</p></div>
          ))}
        </div>
        {inv.credit > 0 && <p data-testid="invoice-credit" className="text-sm text-emerald-700">Credit {money(inv.credit)}</p>}
        <p className="text-xs text-slate-500">Issued {inv.issue_date}{inv.due_date ? ` · Due ${inv.due_date}` : " · Due at shipment"}</p>
        <div className="space-y-1" data-testid="invoice-payments">
          {pays.map((p, i) => (
            <div key={p.id} data-testid={`invoice-payment-${i}`} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 px-3 py-2 text-sm ${p.deleted ? "opacity-50 line-through" : ""}`}>
              <span>{p.date} · <span className="capitalize">{p.method}</span>{p.reference ? ` · ${p.reference}` : ""} · {p.by_name}{p.deleted ? ` · deleted: ${p.deleted_reason}` : ""}</span>
              <span className="flex items-center gap-2"><span className="font-mono-num font-semibold">{money(p.amount)}</span>
                {admin && !p.deleted && <button data-testid={`invoice-payment-delete-${i}`} onClick={() => setDel(p.id)} className="text-xs text-red-500 hover:underline">Delete</button>}</span>
            </div>
          ))}
          {del && <Reason label="Delete payment" testId="payment-delete" onSubmit={(r) => { act(() => api.post(`/invoices/${inv.id}/payments/${del}/delete`, { reason: r }), "Payment deleted"); setDel(null); }} />}
        </div>
        {inv.status !== "void" && <PaymentForm inv={inv} onSaved={update} />}
        {inv.status === "void" && <p data-testid="invoice-void-reason" className="text-sm text-red-600">Void: {inv.void_reason}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="invoice-pdf-button" onClick={() => downloadBlob(`/invoices/${inv.id}/pdf`, `${inv.number}.pdf`).catch((e) => toast.error(errorMessage(e)))} className={btnLine}><Download className="h-3.5 w-3.5" /> Invoice PDF</button>
          {admin && inv.status !== "void" && (voiding
            ? <Reason label="Void invoice" testId="invoice-void" onSubmit={(r) => act(() => api.post(`/invoices/${inv.id}/void`, { reason: r }), "Invoice voided")} />
            : <button data-testid="invoice-void-button" onClick={() => setVoiding(true)} className={`${btnLine} text-red-600`}>Void…</button>)}
        </div>
        {inv.log?.length > 0 && (
          <details className="text-xs text-slate-500" data-testid="invoice-log"><summary className="cursor-pointer">Activity log</summary>
            {inv.log.map((l, i) => <p key={i}>{l.at.slice(0, 16).replace("T", " ")} · {l.by} · {l.action}{l.reason ? ` · ${l.reason}` : ""}</p>)}
          </details>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceList({ invoices, onChanged, prefix = "invoice" }) {
  const [open, setOpen] = useState(null);
  if (!invoices?.length) return <p className="py-4 text-sm text-slate-500" data-testid={`${prefix}-empty`}>No invoices yet.</p>;
  return (
    <div className="divide-y divide-slate-800/60">
      {invoices.map((inv, i) => (
        <button key={inv.id} data-testid={`${prefix}-row-${i}`} onClick={() => setOpen(inv)} className="flex w-full flex-wrap items-center justify-between gap-3 py-3 text-left transition-colors hover:bg-amber-500/5">
          <span className="flex items-center gap-3"><span className="font-mono-num text-sm text-slate-200">{inv.number}</span><span className="text-xs text-slate-500">{inv.label}</span><InvoiceBadge inv={inv} testId={`${prefix}-status-${i}`} /></span>
          <span className="flex items-center gap-4 font-mono-num text-sm"><span className="text-slate-400">{money(inv.amount)}</span><span data-testid={`${prefix}-balance-${i}`} className="font-semibold text-slate-100">Balance {money(inv.balance)}</span></span>
        </button>
      ))}
      {open && <InvoiceDialog invoice={open} onChanged={onChanged} onClose={() => { setOpen(null); onChanged?.(); }} />}
    </div>
  );
}
